import { useNavigate } from "react-router-dom";

const FooterPartner = () => {
  const navigate = useNavigate();
  const partnershipModels = [
    {
      title: "E-Commerce Vendor Partnership",
      for: "Medical device suppliers, health & wellness products, diagnostic kits, supplements, preventive care essentials",
      benefits: [
        "Showcase and sell via LifeOnPlus Marketplace",
        "Real-time inventory, order, and payment dashboard",
        "Integrated logistics and shipping",
        "Vendor protection, return, and refund policy adherence",
        "Auto-payout with GST & TDS compliance",
      ],
    },
    {
      title: "Service Provider Partnership",
      for: "Clinics, diagnostic labs, physiotherapists, dieticians, wellness experts, therapists, hospitals, and fitness centres",
      benefits: [
        "List and manage services on the LifeOnPlus App",
        "GPS-based local visibility",
        "Direct bookings, telehealth slots, doorstep services",
        "Custom pricing and availability options",
        "User feedback and review analytics",
      ],
    },
    {
      title: "Channel Partner Model",
      for: "Entrepreneurs and investors",
      benefits: [
        "Region-wise partner acquisition model",
        "First-come, first-verified selection with KYC",
        "Track sales, stock, and earnings down to the last level",
        "Referral-based network growth system",
        "Commission and wallet dashboard with weekly payout cycles",
      ],
    },
    {
      title: "Franchise & Body Recharge Station Partners",
      for: "Investors and wellness entrepreneurs",
      benefits: [
        "Exclusive license to operate a non-invasive body recharge centre",
        "Complete setup support: technology, interiors, branding, training",
        "Fixed pricing model, consumables stock, and ROI plan",
        "Integration into LifeOnPlus for local booking and visibility",
        "SOP-driven operations and quarterly audit model",
      ],
    },
    {
      title: "Network Marketing & Referral Partners",
      for: "Individuals, influencers, and wellness advocates",
      benefits: [
        "Promote LifeOnPlus digital services and products",
        "Unique referral code system with traceable genealogy",
        "Earn on each referred transaction or registration",
        "Dashboard to track earnings and network tree",
        "Zero investment, high earning potential",
      ],
    },
    {
      title: "Strategic Alliances & Corporate Partners",
      for: "Insurance companies, CSR initiatives, wellness brands, HR wellness arms, NGOs, community groups",
      benefits: [
        "White-labelled services for your beneficiaries",
        "Bulk health card distribution and employee engagement models",
        "CSR integration with real-time impact reporting",
        "Joint campaigns and co-branding",
      ],
    },
  ];

  const howItWorks = [
    "Choose Your Model – Explore and register for the relevant partner category",
    "Complete KYC – Submit necessary documents and get verified",
    "Onboarding & Training – Receive guidelines, login access, and support",
    "Go Live – Begin operations, marketing, and service delivery",
    "Track & Earn – Transparent earnings, stock, and service analytics dashboard",
  ];

  const partnerSupport = [
    "Onboarding kit & manuals",
    "Partner helpline & CRM access",
    "Marketing toolkit",
    "App/web dashboard",
    "Product demos & service SOPs",
    "Weekly webinars and updates",
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans overflow-hidden">
      {/* Header Section */}
      <div className="w-screen h-64 md:h-[430px] border-[3px] border-[#35A93E]">
        <img
          src="/partnerModule.jpeg"
          alt="Contact Banner"
          className="w-full h-full object-contain md:object-contain"
        />
      </div>

      {/* Introduction Section */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Welcome to the LifeOnPlus Partner Ecosystem
        </h2>
        <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto">
          Where health meets technology, and collaboration drives impact. Our
          partner models are designed for mutual growth, transparency, and
          profitability.
        </p>
      </section>

      {/* Why Partner Section */}
      <section className="bg-white py-12 px-4 md:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Why Partner with LifeOnPlus?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {[
            "Access to a rapidly growing digital health user base",
            "Transparent revenue-sharing and real-time tracking",
            "Multi-level support and training",
            "Digital dashboards for business insights",
            "Verified brand credibility with cross-platform visibility",
            "Scalable, region-wise expansion model",
          ].map((item, index) => (
            <div key={index} className="bg-gray-100 p-6 rounded-lg shadow-md">
              <p className="text-lg text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Partnership Models Section */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Our Partnership Models
        </h2>
        <div className="space-y-8">
          {partnershipModels.map((model, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-[#35A93E] mb-2">
                {index + 1}. {model.title}
              </h3>
              <p className="text-gray-600 mb-4">
                <strong>For:</strong> {model.for}
              </p>
              <ul className="list-disc pl-6 space-y-2">
                {model.benefits.map((benefit, i) => (
                  <li key={i} className="text-gray-700">
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-gray-100 py-12 px-4 md:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          How It Works
        </h2>
        <div className="max-w-3xl mx-auto">
          <ol className="list-decimal pl-6 space-y-4">
            {howItWorks.map((step, index) => (
              <li key={index} className="text-lg text-gray-700">
                {step}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Partner Support Section */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Partner Support & Training
        </h2>
        <p className="text-lg text-gray-600 text-center mb-6">
          Every partner is equipped with:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {partnerSupport.map((item, index) => (
            <div key={index} className="bg-white p-4 rounded-lg shadow-md">
              <p className="text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-[#35A93E] text-white py-12 px-4 md:px-8 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Partner?</h2>
        <p className="text-lg mb-6">
          Start your journey with LifeOnPlus and be part of the future of
          preventive and accessible healthcare.
        </p>
        <button
          className="inline-block bg-white text-[#35A93E] font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition"
          onClick={() =>
            navigate("/", { state: { openChannelPartnerSignUp: true } })
          }
        >
          Register as a Partner
        </button>
        <p className="mt-4">
          For more information:{" "}
          <a href="mailto:info@lifeonplus.com" className="underline">
            info@lifeonplus.com
          </a>
        </p>
      </section>
    </div>
  );
};

export default FooterPartner;
